/**
 * Service de persistance des filtres de liste
 * Stocke les filtres dans localStorage sans limite de durée
 */

const STORAGE_PREFIX = 'filters_'

/**
 * Version du schéma de filtres.
 * À incrémenter chaque fois que le comportement par défaut des filtres change
 * pour forcer une purge des anciens filtres sauvegardés en localStorage.
 *
 * v3 : Passage de l'approche URL-based à localStorage-only.
 * v4 : Ajout du filtre endScheduledVisitAt='today' dans les défauts — les lots
 *      récemment visités (scheduledVisitAt dans le futur) sont masqués par défaut.
 * v5 : Ajout du filtre invisible excludeType='Y' — les "Lots Fictifs" (type Y)
 *      sont masqués par défaut dans /parc-immobilier.
 * v6 : Ajout du filtre invisible excludeType=['Y','$'] — les "Chambres celib" (type $)
 *      sont également masquées par défaut dans /parc-immobilier (en plus des lots fictifs).
 */
const FILTERS_VERSION = '6'
const FILTERS_VERSION_KEY = 'filters_schema_version'

/**
 * Filtres appliqués par défaut lors de la première visite (aucun filtre en localStorage).
 * Exporté pour être utilisé dans les vues.
 */
export const DEFAULT_FILTERS = {
  // 'today' est un mot-clé résolu dynamiquement dans dexieService.filterProperties()
  // afin que le filtre soit toujours "aujourd'hui" sans stocker une date figée.
  // excludeType=['Y','$'] : masque les "Lots Fictifs" (Y) et les "Chambres celib" ($) de façon invisible pour l'utilisateur.
  PropertyPortfolio: { status: 'vacant', hasPlanification: 'ok', hasNotPlanification: 'ok', endScheduledVisitAt: 'today', excludeType: ['Y', '$'] },
  ActionsList: { status: 'vacant', hasPlanification: 'ok', hasNotPlanification: 'ok', endScheduledVisitAt: 'today' },
  MyVisites: { status: 'vacant', hasPlanification: 'ok', hasNotPlanification: 'ok', endScheduledVisitAt: 'today' }
}

/**
 * Migration des filtres sauvegardés.
 * Appelé automatiquement au chargement du module.
 */
function migrateFiltersIfNeeded() {
  try {
    const storedVersion = localStorage.getItem(FILTERS_VERSION_KEY)
    if (storedVersion === FILTERS_VERSION) return

    // v3 : réinitialiser tous les filtres pour repartir sur les nouveaux defaults
    const listRoutes = ['PropertyPortfolio', 'MyVisites', 'ActionsList']
    listRoutes.forEach(routeName => {
      localStorage.removeItem(`${STORAGE_PREFIX}${routeName}`)
    })

    localStorage.setItem(FILTERS_VERSION_KEY, FILTERS_VERSION)
  } catch (error) {
    console.warn('[filterStorageService] Erreur lors de la migration des filtres:', error)
  }
}

// Exécuter la migration au chargement du module
migrateFiltersIfNeeded()

/**
 * Sauvegarde les filtres d'une route spécifique
 * @param {String} subject - Identifiant de la route (ex: 'PropertyPortfolio')
 * @param {Object} query - L'objet de filtres à sauvegarder
 */
export function saveFilters(subject, query) {
  try {
    const toSave = {}
    Object.entries(query).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Les chaînes vides sont sauvegardées volontairement : elles signifient
        // "l'utilisateur a explicitement vidé ce filtre" (≠ "jamais défini").
        toSave[key] = value
      }
    })
    const storageKey = `${STORAGE_PREFIX}${subject}`
    if (Object.keys(toSave).length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(toSave))
    } else {
      localStorage.removeItem(storageKey)
    }
  } catch (error) {
    console.warn('[filterStorageService] Erreur lors de la sauvegarde des filtres:', error)
  }
}

/**
 * Charge les filtres sauvegardés pour une route
 * @param {String} subject - Identifiant de la route
 * @returns {Object|null} Les filtres sauvegardés ou null si aucun
 */
export function loadFilters(subject) {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${subject}`)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.warn('[filterStorageService] Erreur lors du chargement des filtres:', error)
  }
  return null
}

/**
 * Supprime les filtres sauvegardés pour une route
 * @param {String} subject - Identifiant de la route
 */
export function clearFilters(subject) {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${subject}`)
  } catch (error) {
    console.warn('[filterStorageService] Erreur lors de la suppression des filtres:', error)
  }
}

/**
 * Charge les filtres depuis localStorage avec fallback sur DEFAULT_FILTERS.
 * Si aucun filtre n'est sauvegardé (première visite), applique et sauvegarde les defaults.
 *
 * @param {String} subject - Identifiant de la route ('PropertyPortfolio', 'MyVisites', 'ActionsList')
 * @returns {Object} Les filtres actifs (depuis localStorage ou defaults)
 */
export function getFiltersWithDefaults(subject) {
  const saved = loadFilters(subject)
  if (saved !== null) {
    return { ...saved }
  }
  // Première visite → appliquer et sauvegarder les defaults
  const defaults = DEFAULT_FILTERS[subject] || {}
  if (Object.keys(defaults).length > 0) {
    saveFilters(subject, defaults)
  }
  return { ...defaults }
}

export default {
  saveFilters,
  loadFilters,
  clearFilters,
  getFiltersWithDefaults
}
